import { createTheme, alpha } from '@mui/material/styles';

// Color palette
const colors = {
  // Primary gradient colors
  primary: {
    main: '#6366f1', // Indigo
    light: '#818cf8',
    dark: '#4f46e5',
  },
  secondary: {
    main: '#ec4899', // Pink
    light: '#f472b6',
    dark: '#db2777',
  },
  // Background colors
  background: {
    gradient: 'linear-gradient(135deg, #0f0c29 0%, #302b63 50%, #24243e 100%)',
    dark: '#0a0a0f',
    card: 'rgba(255, 255, 255, 0.05)',
  },
  // Glass effect colors
  glass: {
    background: 'rgba(255, 255, 255, 0.08)',
    border: 'rgba(255, 255, 255, 0.12)',
    hover: 'rgba(255, 255, 255, 0.12)',
  },
  // Text colors
  text: {
    primary: '#ffffff',
    secondary: 'rgba(255, 255, 255, 0.7)',
    muted: 'rgba(255, 255, 255, 0.5)',
  },
  // Status colors
  success: '#10b981',
  error: '#ef4444',
  warning: '#f59e0b',
};

// Glassmorphism styles (reusable)
export const glassStyles = {
  // Standard glass card
  card: {
    background: colors.glass.background,
    backdropFilter: 'blur(20px)',
    WebkitBackdropFilter: 'blur(20px)',
    border: `1px solid ${colors.glass.border}`,
    borderRadius: '16px',
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
  },
  // Elevated glass card
  cardElevated: {
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(24px)',
    WebkitBackdropFilter: 'blur(24px)',
    border: `1px solid rgba(255, 255, 255, 0.15)`,
    borderRadius: '20px',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.4)',
  },
  // Subtle glass effect
  subtle: {
    background: 'rgba(255, 255, 255, 0.03)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: `1px solid rgba(255, 255, 255, 0.06)`,
    borderRadius: '12px',
  },
  // Glass button
  button: {
    background: 'rgba(255, 255, 255, 0.1)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: `1px solid rgba(255, 255, 255, 0.1)`,
    borderRadius: '12px',
    transition: 'all 0.3s ease',
    '&:hover': {
      background: 'rgba(255, 255, 255, 0.15)',
      border: `1px solid rgba(255, 255, 255, 0.2)`,
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
    },
  },
  // Glass input
  input: {
    background: 'rgba(255, 255, 255, 0.05)',
    backdropFilter: 'blur(10px)',
    WebkitBackdropFilter: 'blur(10px)',
    border: `1px solid rgba(255, 255, 255, 0.1)`,
    borderRadius: '12px',
    '&:hover': {
      border: `1px solid rgba(255, 255, 255, 0.2)`,
    },
    '&:focus-within': {
      border: `1px solid ${colors.primary.main}`,
      boxShadow: `0 0 0 3px ${alpha(colors.primary.main, 0.2)}`,
    },
  },
};

// MUI Theme
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: colors.primary,
    secondary: colors.secondary,
    background: {
      default: colors.background.dark,
      paper: colors.glass.background,
    },
    text: {
      primary: colors.text.primary,
      secondary: colors.text.secondary,
    },
    success: { main: colors.success },
    error: { main: colors.error },
    warning: { main: colors.warning },
  },
  typography: {
    fontFamily: '"Inter", "SF Pro Display", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: {
      fontSize: '2.5rem',
      fontWeight: 700,
      letterSpacing: '-0.02em',
    },
    h2: {
      fontSize: '2rem',
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    h3: {
      fontSize: '1.5rem',
      fontWeight: 600,
    },
    h4: {
      fontSize: '1.25rem',
      fontWeight: 600,
    },
    h5: {
      fontSize: '1rem',
      fontWeight: 600,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.875rem',
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: {
          // Ensure proper rendering on mobile
          WebkitTextSizeAdjust: '100%',
          WebkitTapHighlightColor: 'transparent',
        },
        body: {
          background: colors.background.gradient,
          backgroundAttachment: 'fixed',
          minHeight: '100vh',
          minHeight: '-webkit-fill-available',
          // Prevent overscroll bounce on iOS
          overscrollBehavior: 'none',
          // Safe area padding for notched devices
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
        },
        '#root': {
          minHeight: '100vh',
          minHeight: '-webkit-fill-available',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          padding: '10px 24px',
          fontSize: '0.95rem',
          fontWeight: 500,
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        contained: {
          background: `linear-gradient(135deg, ${colors.primary.main} 0%, ${colors.primary.dark} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${colors.primary.light} 0%, ${colors.primary.main} 100%)`,
          },
        },
        outlined: {
          borderColor: colors.glass.border,
          '&:hover': {
            borderColor: colors.primary.main,
            background: alpha(colors.primary.main, 0.1),
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          ...glassStyles.card,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            ...glassStyles.input,
            '& fieldset': {
              border: 'none',
            },
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          ...glassStyles.subtle,
          border: 'none',
        },
      },
    },
    MuiBottomNavigation: {
      styleOverrides: {
        root: {
          background: 'rgba(15, 15, 20, 0.8)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          borderTop: `1px solid ${colors.glass.border}`,
        },
      },
    },
    MuiBottomNavigationAction: {
      styleOverrides: {
        root: {
          color: colors.text.muted,
          '&.Mui-selected': {
            color: colors.primary.main,
          },
        },
      },
    },
  },
});

export { colors };
export default theme;
